import { useSocialLinks } from 'app/hooks/use-social-links';
import { IconHeartHandshake } from '@tabler/icons-react-native';
import { isLoggedInAtom, userInfoAtom } from 'app/atoms';
import { AmbassadorMedal } from 'app/design/icons/ambassador-medal';
import { SuperSharerMedal } from 'app/design/icons/super-sharer-medal';
import { useAmbassador } from 'app/hooks/use-ambassador';
import { selectAvatarBadge, type AvatarBadgeKind } from 'app/lib/avatar-badge';
import { useTrustCircle } from 'app/network/profile';
import { useTheme } from 'app/shopify';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { View, type StyleProp, type ViewStyle } from 'react-native';

export interface UserAvatarBadgeProps {
  userId?: string;
  isAmbassador?: boolean | null;
  isSuper?: boolean | null;
  avatarSize: number;
  size?: number;
  style?: StyleProp<ViewStyle>;
}

/** All avatar-attached membership/status badges must go through this component. */
export function UserAvatarBadge({ userId, isAmbassador, isSuper, avatarSize, size, style }: UserAvatarBadgeProps) {
  const viewer = useAtomValue(userInfoAtom);
  const loggedIn = useAtomValue(isLoggedInAtom);
  const socialLinks = useSocialLinks();
  const ambassador = useAmbassador();
  const circle = useTrustCircle({
    enabled: socialLinks.enabled && loggedIn && Boolean(userId) && userId !== viewer?.id,
    staleTime: 60_000,
  });
  const isInTrustCircle =
    socialLinks.enabled &&
    loggedIn &&
    userId !== viewer?.id &&
    Boolean(circle.data?.some((member) => member.userId === userId));
  const kind = selectAvatarBadge({ isInTrustCircle, isAmbassador: ambassador.isEnabled && isAmbassador, isSuper });
  return kind ? <AvatarBadge kind={kind} avatarSize={avatarSize} size={size} style={style} /> : null;
}

export function AvatarBadge({
  kind,
  avatarSize,
  size: requestedSize,
  style,
}: {
  kind: AvatarBadgeKind;
  avatarSize: number;
  size?: number;
  style?: StyleProp<ViewStyle>;
}) {
  const size = requestedSize ?? Math.round(avatarSize * 0.65);
  const theme = useTheme();
  const { t } = useTranslation();
  const trustSize = Math.max(16, Math.round(avatarSize * 0.42));
  const label = {
    'trust-circle': t('trust.badge', { ns: 'neighbour-profile' }),
    ambassador: t('ambassadorLabel', { ns: 'neighbour-profile' }),
    'super-sharer': t('superPartageur', { ns: 'common' }),
  }[kind];
  return (
    <View
      pointerEvents="none"
      accessible
      accessibilityRole="image"
      accessibilityLabel={label}
      style={[
        {
          position: 'absolute',
          bottom: kind === 'trust-circle' ? 0 : -Math.round(size * 0.3),
          right: kind === 'trust-circle' ? 0 : -Math.round(avatarSize * 0.1),
          zIndex: 2,
        },
        style,
      ]}
    >
      {kind === 'trust-circle' ? (
        <View
          style={{
            width: trustSize,
            height: trustSize,
            borderRadius: trustSize / 2,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.colors['surface.brand.secondary-4.subtle'],
          }}
        >
          <IconHeartHandshake
            size={Math.round(trustSize * 0.65)}
            color={theme.colors['text.primary']}
            strokeWidth={ICON_STROKE_WIDTH}
          />
        </View>
      ) : kind === 'ambassador' ? (
        <AmbassadorMedal width={size} height={size * (60 / 57)} />
      ) : (
        <SuperSharerMedal width={size} height={size * (69 / 57)} />
      )}
    </View>
  );
}
